/**
 * A schedule as a person states it ("every day at 06:00, Europe/Zurich") and
 * its conversion to the five-field UTC cron the backend stores.
 *
 * The backend evaluates cron in UTC and has no column for a zone, so the
 * zone lives only in the form: the clock time is shifted by the zone's
 * offset when the cron is built, and shifted back when a stored cron is
 * opened. The offset is the zone's offset today, so a schedule set during
 * daylight-saving time drifts an hour when the clocks change; persisting the
 * zone is the fix, and it is a backend change.
 */

import { DateTime } from "luxon";

export type SchedulePreset = "hourly" | "daily" | "weekly" | "custom";

export interface ScheduleDraft {
  preset: SchedulePreset;
  /** Clock time "HH:MM" in `timeZone`; unused by `hourly` beyond its minutes. */
  time: string;
  /** ISO weekday, 1 = Monday … 7 = Sunday; `weekly` only. */
  weekday: number;
  /** IANA zone the time is stated in. */
  timeZone: string;
  /** The raw expression; `custom` only, and always UTC. */
  cron: string;
}

export const DEFAULT_SCHEDULE_TIME = "09:00";
export const DEFAULT_SCHEDULE_WEEKDAY = 1;

const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** Every zone the runtime knows, UTC first; the browser's zone is always included. */
export function timeZoneOptions(): string[] {
  const known: string[] =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];
  const zones = new Set(["UTC", browserTimeZone(), ...known]);
  return [...zones];
}

export function newScheduleDraft(timeZone = browserTimeZone()): ScheduleDraft {
  return {
    preset: "daily",
    time: DEFAULT_SCHEDULE_TIME,
    weekday: DEFAULT_SCHEDULE_WEEKDAY,
    timeZone,
    cron: "",
  };
}

function parseTime(time: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (match === null) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) {
    return null;
  }
  return { hour, minute };
}

/**
 * The UTC cron a draft stores, or `null` when the draft is not yet valid
 * (a malformed time). A custom draft is passed through untouched.
 */
export function scheduleToUtcCron(draft: ScheduleDraft): string | null {
  if (draft.preset === "custom") {
    return draft.cron.trim().length > 0 ? draft.cron.trim() : null;
  }
  const clock = parseTime(draft.time);
  if (clock === null) {
    return null;
  }
  const local = DateTime.fromObject(
    { weekday: draft.weekday as 1 | 2 | 3 | 4 | 5 | 6 | 7, ...clock },
    { zone: draft.timeZone },
  );
  if (!local.isValid) {
    return null;
  }
  const utc = local.toUTC();
  switch (draft.preset) {
    case "hourly":
      return `${utc.minute} * * * *`;
    case "daily":
      return `${utc.minute} ${utc.hour} * * *`;
    case "weekly":
      return `${utc.minute} ${utc.hour} * * ${utc.weekday % 7}`;
  }
}

const FIELD = /^\d{1,2}$/;

/**
 * Reads a stored UTC cron back into a draft stated in `timeZone`. Only the
 * shapes the presets write are recognized; anything else is a custom
 * expression, shown as-is.
 */
export function scheduleFromUtcCron(
  cron: string,
  timeZone: string,
): ScheduleDraft {
  const custom: ScheduleDraft = {
    preset: "custom",
    time: DEFAULT_SCHEDULE_TIME,
    weekday: DEFAULT_SCHEDULE_WEEKDAY,
    timeZone,
    cron,
  };
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) {
    return custom;
  }
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  if (dayOfMonth !== "*" || month !== "*" || !FIELD.test(minute)) {
    return custom;
  }
  if (hour === "*" && dayOfWeek === "*") {
    const local = DateTime.utc()
      .set({ minute: Number(minute) })
      .setZone(timeZone);
    return { ...custom, preset: "hourly", time: clockOf(local) };
  }
  if (!FIELD.test(hour)) {
    return custom;
  }
  if (dayOfWeek === "*") {
    const local = DateTime.utc()
      .set({ hour: Number(hour), minute: Number(minute) })
      .setZone(timeZone);
    return { ...custom, preset: "daily", time: clockOf(local) };
  }
  if (FIELD.test(dayOfWeek) && Number(dayOfWeek) <= 7) {
    const cronDay = Number(dayOfWeek) % 7;
    const local = DateTime.fromObject(
      {
        weekday: (cronDay === 0 ? 7 : cronDay) as 1 | 2 | 3 | 4 | 5 | 6 | 7,
        hour: Number(hour),
        minute: Number(minute),
      },
      { zone: "utc" },
    ).setZone(timeZone);
    return {
      ...custom,
      preset: "weekly",
      time: clockOf(local),
      weekday: local.weekday,
    };
  }
  return custom;
}

function clockOf(moment: DateTime): string {
  return moment.toFormat("HH:mm");
}

/** "Every Monday at 09:00 (Europe/Zurich)": the draft read back. */
export function readableSchedule(draft: ScheduleDraft): string {
  switch (draft.preset) {
    case "hourly": {
      const clock = parseTime(draft.time);
      return clock === null || clock.minute === 0
        ? "Every hour, on the hour"
        : `Every hour at ${String(clock.minute).padStart(2, "0")} past (${draft.timeZone})`;
    }
    case "daily":
      return `Every day at ${draft.time} (${draft.timeZone})`;
    case "weekly":
      return `Every ${WEEKDAY_NAMES[draft.weekday - 1]} at ${draft.time} (${draft.timeZone})`;
    case "custom":
      return `Cron ${draft.cron} (UTC)`;
  }
}

export function weekdayName(weekday: number): string {
  return WEEKDAY_NAMES[weekday - 1] ?? "";
}
